import React, { useMemo, useState } from "react";
import { Header, Card } from "semantic-ui-react";
import { AppTable, FlexBox, FlexRow, LoaderBar } from "../../components";
import { PencilSimple } from "phosphor-react";
import { useTabletAndBelowMediaQuery } from "layout";
import { AppPrimaryButton } from "elements";
import { DateFormat, DateUtility } from "utility";
import ActionBoxes from "components/ActionBoxes";
import { GetPermissionList } from "hooks/permission";
import PermissionModal from "./PermissionModal";
import styled from "styled-components";

const GroupCardMetaRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 6px;
    font-size: 13px;

    .label {
        font-weight: 600;
        color: #334155;
        min-width: 110px;
        flex-shrink: 0;
    }

    .value {
        color: #0f172a;
        font-weight: 500;
    }
`;

const PermissionCard = ({ item, handleEdit }) => (
    <Card fluid className="mb-3">
        <Card.Content>
            <Card.Header style={{ fontWeight: 600, color: '#0f172a' }}>
                {item.PermissionName}
            </Card.Header>
            <Card.Description className="mt-2">
                <GroupCardMetaRow>
                    <span className="label">Permission ID</span>
                    <span className="value">{item.PermissionId}</span>
                </GroupCardMetaRow>
                <GroupCardMetaRow>
                    <span className="label">Permission Key</span>
                    <span className="value">{item.PermissionKey}</span>
                </GroupCardMetaRow>
                <GroupCardMetaRow>
                    <span className="label">Category</span>
                    <span className="value">{item.Category || '(none)'}</span>
                </GroupCardMetaRow>
                <GroupCardMetaRow>
                    <span className="label">Created On</span>
                    <span className="value">
                        {item.CreatedOn &&
                            DateUtility.dateToLocalISO(
                                new Date(item.CreatedOn),
                                DateFormat.date,
                            )}
                    </span>
                </GroupCardMetaRow>
                <FlexBox className="justify-content-end mt-3">
                    <ActionBoxes
                        item={item}
                        menuItems={[
                            {
                                label: "Edit",
                                icon: PencilSimple,
                                onClick: () => handleEdit(item),
                            },
                        ]}
                    />
                </FlexBox>
            </Card.Description>
        </Card.Content>
    </Card>
);

export const PermissionsScreen = () => {
    const { data, refresh, loading } = GetPermissionList();

    const isMobile = useTabletAndBelowMediaQuery();

    const [modalOpen, setModalOpen] = useState(false);
    const [editItem, setEditItem] = useState(null);

    const handleEdit = (item) => {
        setEditItem(item);
        setModalOpen(true);
    };

    const handleAdd = () => {
        setEditItem(null);
        setModalOpen(true);
    };

    const columns = useMemo(
        () => [
            {
                title: "Permission Id",
                key: "PermissionId",
            },
            {
                title: "Permission Name",
                key: "PermissionName",
            },
            {
                title: "Permission Key",
                key: "PermissionKey",
            },
            {
                title: "Category",
                key: "Category",
            },
            {
                title: "Created On",
                render: (item) => (
                    <span>
                        {DateUtility.dateToLocalISO(
                            new Date(item.CreatedOn),
                            DateFormat.date,
                        )}
                    </span>
                ),
            },
            {
                title: "Actions",
                render: (item) => (
                    <FlexBox className="justify-content-center">
                        <ActionBoxes
                            item={item}
                            menuItems={[
                                {
                                    label: "Edit",
                                    icon: PencilSimple,
                                    onClick: () => handleEdit(item),
                                },
                            ]}
                        />
                    </FlexBox>
                ),
            },
        ],
        [data, isMobile],
    );

    return (
        <>
            <Header as="h3">Configured Permissions</Header>
            {/* Add Button */}
            <FlexRow className="justify-content-end mb-3">
                <AppPrimaryButton onClick={handleAdd}>
                    Add Permission
                </AppPrimaryButton>
            </FlexRow>

            {loading ? (
                <LoaderBar />
            ) : (
                <AppTable
                    data={data?.length > 0 ? data : []}
                    columns={columns}
                    pageChanged={() => { }}
                    filter=""
                    totalCount={0}
                    hidePagination="true"
                    mobileCard={<PermissionCard handleEdit={handleEdit} />}
                />
            )}

            {/* Add / Edit Modal */}
            <PermissionModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                editItem={editItem}
                refresh={refresh}
            />
        </>
    );
};